// Where a recording waits on this device before the server has it:
// IndexedDB, so a reload, a crash, a closed tab or a quit app loses nothing
// that was already captured. Every piece of audio is written here BEFORE it
// is sent, and removed only once the server has it (part 0, the container
// header, stays until the take is done: recorderModel.indexFor).
//
// When IndexedDB is missing, refuses (a private window, storage blocked,
// the disk full) or does not open within STORE_OPEN_MS (WebKit can hang on
// it), the same interface keeps everything in memory, and the recorder says
// so ("keep this page open until it has uploaded").
//
// Opened only when it is needed (a recording starts, a signed-in page looks
// for what a closed page left, or the Mac app asks what is stored), and
// persistent storage is asked for only when a recording starts: a page that
// never records never touches it. A connection the browser drops (Safari,
// after the app was in the background) is opened again once per call.

import type { RecordingInterruption } from "@/api/meetingRecordings";
import type { UploadRef } from "./recorderModel";

export type TakeStatus =
  | "capturing"  // the microphone is live in the tab that owns it
  | "closing"    // Stop was pressed (or it ended): send the rest, then finish
  | "held"       // cut short (the page closed): send the rest, finish at `finishAt` unless carried on
  | "kept"       // finished, but Corvus refused some parts (part_exists) or found no audio (empty_recording): kept here to save as a file
  | "done";      // finished on the server (kept briefly so the screen can say so)

export interface TakeRecord {
  /** The take's client_ref: stable for its whole life, across new uploads and reloads. */
  id: string;
  customerId: string;
  /** The meeting the server filed it under (once registered), else the one picked. */
  meetingId: string | null;
  title: string | null;
  /** The meeting's own title and end, for the words on screen and the auto-finish time. */
  meetingTitle: string | null;
  meetingEndAt: string | null;
  mime: string;
  continueRecording: boolean;
  /** The upload the parts go to (null until registered). */
  upload: UploadRef | null;
  /** The upload ids this take used before `upload` (finished by the server; late parts go there). */
  formerUploads: string[];
  /** Every upload this take has used, with its base: one the server hands back again is resumed exactly. */
  uploads?: UploadRef[];
  /** Parts the server already held different audio for (part_exists): kept here, never deleted with the take. */
  keptParts?: number[];
  /** A take carried on from this one (same meeting): it waits for this one's meeting id. */
  followsTake: string | null;
  segment: number;
  startedAt: number;
  lastAudioAt: number;
  audioSec: number;
  nextSeq: number;
  /** Every part below this seq has reached the server (sends are in order). */
  sentBelow: number;
  status: TakeStatus;
  cutShortAt: number | null;
  cutReason: string | null;
  finishAt: number | null;
  interruptions: RecordingInterruption[];
  /** The tab capturing or sending it, and when that tab last said so. */
  ownerTab: string;
  aliveAt: number;
  /**
   * Who recorded it (recorderModel.ownerKey: role, user, a child's agent). Only that
   * sign-in takes it over, sends it, shows it or offers it; another sign-in on this
   * browser leaves it for its owner. Missing on a record from before owners were kept:
   * an operator's of its workspace.
   */
  owner?: string;
  /** A carry-on: when the first take of the run it carries on began (the Mac app's clock goes on from it). */
  chainStartedAt?: number;
  /** The take that carries this one on: this one is finished only once that one is filed (one set of notes). */
  finishAfter?: string | null;
  /** Corvus answered that it holds no audio for it (empty_recording), while this device still has some: kept here. */
  noAudio?: boolean;
  /** A call recording (the call's shared sound with the microphone): carried on only from a tap, sharing again. */
  callSound?: boolean;
}

export interface PartRecord {
  takeId: string;
  seq: number;
  data: Blob;
}

export interface RecorderStore {
  /** "device" when a reload keeps what is stored; "memory" when only this page does. */
  readonly kind: "device" | "memory";
  /**
   * Memory standing in for a device store that did not open in time: what that
   * store holds (a closed page's takes) is not seen, so the page is not `bound`
   * (I3), and IndexedDB is tried again at the next look.
   */
  readonly unopened?: boolean;
  listTakes(): Promise<TakeRecord[]>;
  putTake(t: TakeRecord): Promise<void>;
  deleteTake(id: string): Promise<void>;
  putPart(p: PartRecord): Promise<void>;
  getPart(takeId: string, seq: number): Promise<PartRecord | null>;
  deletePart(takeId: string, seq: number): Promise<void>;
  listPartSeqs(takeId: string): Promise<number[]>;
  /** Ask the browser not to evict this store (called when a recording starts). */
  persist?(): void;
}

/** Everything in this page's memory: the fallback (`unopened`: IndexedDB did not open in time), and the self-test's store. */
export function memoryStore(o: { unopened?: boolean } = {}): RecorderStore {
  const takes = new Map<string, TakeRecord>();
  const parts = new Map<string, PartRecord>();
  const key = (t: string, s: number) => `${t}\u0000${s}`;
  return {
    kind: "memory",
    unopened: o.unopened === true,
    listTakes: async () => [...takes.values()].map((t) => structuredCopy(t)),
    putTake: async (t) => void takes.set(t.id, structuredCopy(t)),
    deleteTake: async (id) => {
      takes.delete(id);
      for (const k of [...parts.keys()]) if (k.startsWith(`${id}\u0000`)) parts.delete(k);
    },
    putPart: async (p) => void parts.set(key(p.takeId, p.seq), p),
    getPart: async (t, s) => parts.get(key(t, s)) ?? null,
    deletePart: async (t, s) => void parts.delete(key(t, s)),
    listPartSeqs: async (t) =>
      [...parts.values()].filter((p) => p.takeId === t).map((p) => p.seq).sort((a, b) => a - b),
  };
}

function structuredCopy<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

const DB_NAME = "corvus-recorder";
const TAKES = "takes";
const PARTS = "parts";

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error("IndexedDB request failed"));
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(TAKES)) db.createObjectStore(TAKES, { keyPath: "id" });
      if (!db.objectStoreNames.contains(PARTS)) {
        const parts = db.createObjectStore(PARTS, { keyPath: ["takeId", "seq"] });
        parts.createIndex("takeId", "takeId");
      }
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error("IndexedDB open failed"));
    r.onblocked = () => reject(new Error("IndexedDB blocked"));
  });
}

/** How long opening IndexedDB may take: WebKit has an open that never completes, and nothing may wait on it for ever. */
export const STORE_OPEN_MS = 5_000;

type Timer = (fn: () => void, ms: number) => unknown;
const realTimer: Timer = (fn, ms) => setTimeout(fn, ms);

/** `p`, or a rejection once `ms` has passed. */
export function withinMs<T>(p: Promise<T>, ms: number, timer: Timer = realTimer): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    timer(() => reject(new Error("IndexedDB did not answer in time")), ms);
    p.then(resolve, reject);
  });
}

/**
 * The store over `open` (IndexedDB's open): IndexedDB when it opens within
 * STORE_OPEN_MS, else memory (the recording says so, and the Mac app is told
 * `storage: "memory"`); a connection opened again later is held to the same limit.
 */
export async function storeFrom(open: () => Promise<IDBDatabase>, timer: Timer = realTimer): Promise<RecorderStore> {
  const within = () => withinMs(open(), STORE_OPEN_MS, timer);
  try {
    return idbStore(await within(), within);
  } catch {
    return memoryStore({ unopened: true });
  }
}

function idbStore(first: IDBDatabase, reopen: () => Promise<IDBDatabase>): RecorderStore {
  let db: Promise<IDBDatabase> = Promise.resolve(first);
  // One call; when the connection is gone (closed by the browser), open it again and try once more.
  const run = async <T>(fn: (d: IDBDatabase) => Promise<T>): Promise<T> => {
    try {
      return await fn(await db);
    } catch {
      db = reopen();
      return fn(await db);
    }
  };
  const tx = (d: IDBDatabase, stores: string[], mode: IDBTransactionMode) => d.transaction(stores, mode);
  return {
    kind: "device",
    listTakes: () => run(async (d) => (await req(tx(d, [TAKES], "readonly").objectStore(TAKES).getAll())) as TakeRecord[]),
    putTake: (t) =>
      run(async (d) => {
        const x = tx(d, [TAKES], "readwrite");
        x.objectStore(TAKES).put(t);
        await done(x);
      }),
    deleteTake: (id) =>
      run(async (d) => {
        const x = tx(d, [TAKES, PARTS], "readwrite");
        x.objectStore(TAKES).delete(id);
        x.objectStore(PARTS).delete(IDBKeyRange.bound([id, -Infinity], [id, Infinity]));
        await done(x);
      }),
    putPart: (p) =>
      run(async (d) => {
        const x = tx(d, [PARTS], "readwrite");
        x.objectStore(PARTS).put(p);
        await done(x);
      }),
    getPart: (takeId, seq) =>
      run(async (d) => ((await req(tx(d, [PARTS], "readonly").objectStore(PARTS).get([takeId, seq]))) as PartRecord | undefined) ?? null),
    deletePart: (takeId, seq) =>
      run(async (d) => {
        const x = tx(d, [PARTS], "readwrite");
        x.objectStore(PARTS).delete([takeId, seq]);
        await done(x);
      }),
    listPartSeqs: (takeId) =>
      run(async (d) => {
        const keys = (await req(tx(d, [PARTS], "readonly").objectStore(PARTS).index("takeId").getAllKeys(takeId))) as unknown as Array<[string, number]>;
        return keys.map((k) => k[1]).sort((a, b) => a - b);
      }),
    persist() {
      // Granted silently or not at all in most browsers; asked only once a recording starts.
      void navigator.storage?.persist?.().catch(() => false);
    },
  };
}

let opened: Promise<RecorderStore> | null = null;

/**
 * This browser's recorder store: IndexedDB when it works (and opens in time),
 * else memory. A store that did not open in time is not kept: the next call
 * tries IndexedDB again.
 */
export function deviceStore(): Promise<RecorderStore> {
  if (!opened) {
    const p = typeof indexedDB === "undefined" ? Promise.resolve(memoryStore()) : storeFrom(openDb);
    opened = p;
    void p.then((s) => {
      if (s.unopened && opened === p) opened = null;
    });
  }
  return opened;
}
